import { DateTime, Settings } from 'luxon';

import { timezonesAbbrs } from './timezonesAbbrs';

type BatchingAnchor = 'MONTHDAY' | 'WEEKDAY' | 'YEARDAY';
export interface BatchingConfiguration {
  id: number;
  anchor: BatchingAnchor;
  anchorMonth?: {
    values: number[];
  };
  anchorWeek?: {
    values: number[];
  };
  anchorYear?: {
    values: {
      month: number;
      day: number;
    }[];
  };
  minimumDaysBetweenOrders: number;
  sellingPlanGroup: {
    gId: string;
  };
  shopDomain: string;
}

const intervalByAnchor: Record<BatchingAnchor, string> = {
  MONTHDAY: 'months',
  WEEKDAY: 'weeks',
  YEARDAY: '',
};

const dayOfAnchor: Record<BatchingAnchor, string> = {
  MONTHDAY: 'day',
  WEEKDAY: 'weekday',
  YEARDAY: 'yearday',
};

type AnchorNumber = number;
type AnchorObject = { month: number; day: number };
type Anchor = AnchorNumber | AnchorObject;

export const getBatchingAnchors = ({
  anchor,
  anchorMonth,
  anchorWeek,
  anchorYear,
}: BatchingConfiguration): Anchor[] => {
  let values: Anchor[] | undefined = undefined;
  if (anchor === 'MONTHDAY' && anchorMonth) {
    values = anchorMonth.values;
  } else if (anchor === 'WEEKDAY' && anchorWeek) {
    values = anchorWeek.values;
  }

  if (values) {
    return (values as AnchorNumber[]).sort(
      (anchorA, anchorB) => anchorA - anchorB,
    );
  } else if (anchor === 'YEARDAY' && anchorYear) {
    return anchorYear.values.sort((a, b) => a.month - b.month || a.day - b.day);
  }
  throw new Error(`no anchors were found for anchor ${anchor}`);
};

export const getBatchingOriginDate = ({
  now,
  isRetry,
  originalNextBillingDate,
}: {
  now: DateTime;
  originalNextBillingDate?: string;
  isRetry?: boolean;
}) =>
  isRetry && originalNextBillingDate
    ? DateTime.fromISO(originalNextBillingDate)
    : now;

export const getDatePlusInterval = ({
  billingInterval,
  billingCount,
  date,
}: {
  billingInterval: string;
  billingCount: string;
  date: DateTime;
}) =>
  date.plus({
    [`${billingInterval.toLowerCase()}s`]: billingCount,
  });

export const getLaterDate = ({
  dateA,
  dateB,
}: {
  dateA: DateTime;
  dateB: DateTime;
}) => (dateB.diff(dateA).toMillis() > 0 ? dateB : dateA);

export const getAnchorDateDay = ({
  anchor,
  date,
}: {
  anchor: string;
  date: DateTime;
}) => date.get(dayOfAnchor[anchor]);

export const getAnchorsBeforeAndAfter = ({
  anchor,
  anchors,
  date,
}: {
  anchor: BatchingAnchor;
  anchors: Anchor[];
  date: DateTime;
}) => {
  let anchorsBefore: Anchor[];
  let anchorsAfter: Anchor[];
  if (anchor !== 'YEARDAY') {
    const dateDay = getAnchorDateDay({ anchor, date });
    anchorsBefore =
      (anchors as AnchorNumber[]).filter((anchor) => anchor < dateDay) || [];
    anchorsAfter =
      (anchors as AnchorNumber[]).filter((anchor) => anchor >= dateDay) || [];
  } else {
    const anchorsAsDate = (anchors as AnchorObject[]).map((a) =>
      DateTime.utc(date.year, a.month, a.day),
    );
    anchorsBefore = anchorsAsDate
      .filter((a) => a < date)
      .map((a) => ({ month: a.month, day: a.day }));

    anchorsAfter = [
      ...anchorsAsDate
        .filter((a) => a >= date)
        .map((a) => ({ month: a.month, day: a.day })),
      ...anchorsBefore,
    ];
  }

  if (!anchorsBefore.length && anchorsAfter.length) {
    anchorsBefore = [anchorsAfter[anchorsAfter.length - 1]];
  }

  if (!anchorsAfter.length && anchorsBefore.length) {
    anchorsAfter = [anchorsBefore[0]];
  }

  return {
    anchorsBefore,
    anchorsAfter,
  };
};

/**
 * This function returns the next anchor date given a date and a list of
 * anchors. `anchor` property tells us if month day numbers or week day numbers
 * are being used, so for example if it's week days and anchors are Monday and
 * Thursday, they will be 1 and 4. And if it's month days maybe it comes 10, 20
 * and 30 of each month.
 *
 * Once we get the anchor to use and its week day or month day, if that number
 * is smaller than the week day or the month day of the provided date, we need
 * to move the date one week or one month beyond, and then set the same week
 * day or month day. In the other case where the anchor is bigger, we don't
 * need to add anything and just set the right week day or month day.
 */
export const getNextAnchorDate = ({
  anchor,
  anchors,
  date,
}: {
  anchor: BatchingAnchor;
  anchors: Anchor[];
  date: DateTime;
}): DateTime => {
  if (anchor === 'YEARDAY') {
    let updatedDate: DateTime;
    for (const { month, day } of anchors as AnchorObject[]) {
      updatedDate = date.set({ month, day });
      if (updatedDate > date) {
        return updatedDate;
      }
    }

    const { month, day } = (anchors as AnchorObject[]).sort(
      (a, b) => a.month - b.month || a.day - b.day,
    )[0] as AnchorObject;

    return date.set({ month, day, year: date.year + 1 });
  }

  const dateDay = getAnchorDateDay({ anchor, date });
  const anchorDay = anchors[0] as AnchorNumber;

  return anchorDay < dateDay
    ? date
        .plus({ [intervalByAnchor[anchor]]: 1 })
        .set({ [dayOfAnchor[anchor]]: anchorDay })
    : date.set({ [dayOfAnchor[anchor]]: anchorDay });
};

/**
 * This function returns the previous anchor date given a date and a list of
 * anchors. `anchor` property tells us if month day numbers or week day numbers
 * are being used, so for example if it's week days and anchors are Monday and
 * Thursday, they will be 1 and 4. And if it's month days maybe it comes 10, 20
 * and 30 of each month.
 *
 * Once we get the anchor to use and its week day or month day, if that number
 * is bigger than the week day or the month day of the provided date, we need
 * to move the date one week or one month back, and then set the same week
 * day or month day. In the other case where the anchor is smaller or equal, we
 * don't need to add anything and just set the right week day or month day.
 */
export const getPreviousAnchorDate = ({
  anchor,
  anchors,
  date,
}: {
  anchor: BatchingAnchor;
  anchors: Anchor[];
  date: DateTime;
}): DateTime => {
  if (anchor === 'YEARDAY') {
    const { month, day } = anchors[anchors.length - 1] as AnchorObject;
    const updatedDate = date.set({ month, day });

    return updatedDate > date
      ? updatedDate.set({ year: updatedDate.year - 1 })
      : updatedDate;
  }

  const dateDay = getAnchorDateDay({ anchor, date });
  const anchorDay = (anchors as AnchorNumber[])[anchors.length - 1];

  return anchorDay > dateDay
    ? date
        .minus({ [intervalByAnchor[anchor]]: 1 })
        .set({ [dayOfAnchor[anchor]]: anchorDay })
    : date.set({ [dayOfAnchor[anchor]]: anchorDay });
};

export const getClosestFutureAnchorBetweenTwoAnchors = ({
  closestAnchorAfterDate,
  closestAnchorBeforeDate,
  date,
  now,
}: {
  closestAnchorAfterDate: DateTime;
  closestAnchorBeforeDate: DateTime;
  date: DateTime;
  now: DateTime;
}) => {
  const daysBetweenBeforeDateAndDate = date.diff(
    closestAnchorBeforeDate,
    'days',
  ).days;
  const daysBetweenAfterDateAndDate = closestAnchorAfterDate.diff(
    date,
    'days',
  ).days;
  const isClosestAnchorBeforeDateFromThePast =
    now.diff(closestAnchorBeforeDate).toMillis() > 0;
  const isClosestAnchorAfterDateCloser =
    daysBetweenBeforeDateAndDate > daysBetweenAfterDateAndDate;

  return isClosestAnchorBeforeDateFromThePast || isClosestAnchorAfterDateCloser
    ? closestAnchorAfterDate
    : closestAnchorBeforeDate;
};

export const getDaysBetween = ({
  dateA,
  dateB,
}: {
  dateA: DateTime;
  dateB: DateTime;
}) => dateA.diff(dateB, 'days').toObject().days as number;

export const getDateWithMinimumDaysBetweenOrders = ({
  closestAnchorToUse,
  daysBetweenClosestAnchorToUseAndNow,
  minimumDaysBetweenOrders,
}: {
  closestAnchorToUse: DateTime;
  daysBetweenClosestAnchorToUseAndNow: number;
  minimumDaysBetweenOrders: number;
}) =>
  daysBetweenClosestAnchorToUseAndNow < minimumDaysBetweenOrders
    ? closestAnchorToUse.plus({
        days: minimumDaysBetweenOrders - daysBetweenClosestAnchorToUseAndNow,
      })
    : closestAnchorToUse;

export const areTwoDatesSameDay = ({
  dateA,
  dateB,
}: {
  dateA: DateTime | string;
  dateB: DateTime | string;
}) =>
  getDaysBetween({
    dateA: typeof dateA === 'string' ? DateTime.fromISO(dateA) : dateA,
    dateB: typeof dateB === 'string' ? DateTime.fromISO(dateB) : dateB,
  }) === 0;

// We store shopDomainOffset from Shopify (https://shopify.dev/api/admin-graphql/2022-04/objects/Shop#fields)
// and it comes as "+0200" without the ":" sign, thanks Shopify :)
export const getParsedShopDomainOffset = (shopDomainOffset: string): string =>
  shopDomainOffset.length === 5
    ? `${shopDomainOffset.slice(0, 3)}:${shopDomainOffset.slice(-2)}`
    : '';

export const getTimezoneGMTOffsetFromShopDomainTZ = (
  shopDomainTZ?: string,
): string =>
  timezonesAbbrs.find(
    (timezoneAbbr) =>
      timezoneAbbr.abbr.toLowerCase() === shopDomainTZ?.toLowerCase(),
  )?.gmt || '';

/**
 * This function calculates the next billing date for subscription contracts
 * that has batching configuration. For doing so, we need to do these steps:
 *
 *   1. Get the origin date or the order, taking into account if it's a retry
 *      or not. In the case it's a retry we use the original next billing date
 *      as we are coming from the dunning process.
 *   2. Add the interval of the batching configuration to that date.
 *   3. Check if date from step 2 is from the past, as it can happen with
 *      dunning process, and if so, use the current time.
 *   4. Get anchor dates before and after the date from step 3.
 *   5. Get the right anchor to use between the two from step 4. The closest
 *      one will be used, except that the closest one is from the past, in that
 *      case we will use the one that is after the date.
 *   6. Check minimum days between orders to see if the anchor date from step 5
 *      is right, and if not, add the missing amount of days.
 *   7. If in step 6 we added extra days to match minimum days between orders,
 *      we need to get the new anchor date, but this time for sure after that
 *      date, as we cannot go back because of minimum days between orders. Once
 *      we have that new anchor date, that's the one to return.
 *      In case that step 6 was fine and no need to add days, that anchor is
 *      the right one and we return it.
 *   8. Lastly we check if the selected day is same day as current date, and if
 *      so we add 2 hours to that date to avoid problems with not billing in
 *      the same hour.
 */
export const getBatchingNextBillingDate = ({
  billingInterval,
  billingCount,
  originalNextBillingDate,
  batchingConfiguration,
  isRetry,
  shopDomainTZ,
  shopDomainOffset,
}: {
  billingInterval: string;
  billingCount: string;
  originalNextBillingDate?: string;
  batchingConfiguration: BatchingConfiguration;
  isRetry?: boolean;
  shopDomainTZ?: string;
  shopDomainOffset?: string;
}) => {
  const gmtOffset = shopDomainOffset
    ? getParsedShopDomainOffset(shopDomainOffset)
    : getTimezoneGMTOffsetFromShopDomainTZ(shopDomainTZ);

  Settings.defaultZone = `utc${gmtOffset}`;

  const now = DateTime.now();
  const { anchor, minimumDaysBetweenOrders } = batchingConfiguration;
  const anchors = getBatchingAnchors(batchingConfiguration);
  const originDate = getBatchingOriginDate({
    now,
    isRetry,
    originalNextBillingDate,
  });

  const originDatePlusInterval = getDatePlusInterval({
    billingInterval,
    billingCount,
    date: originDate,
  });
  const originDateToUse = getLaterDate({
    dateA: originDatePlusInterval,
    dateB: now,
  });
  const { anchorsBefore, anchorsAfter } = getAnchorsBeforeAndAfter({
    anchor,
    anchors,
    date: originDateToUse,
  });
  const closestAnchorBeforeDate = getPreviousAnchorDate({
    anchor,
    anchors: anchorsBefore,
    date: originDateToUse,
  });
  const closestAnchorAfterDate = getNextAnchorDate({
    anchor,
    anchors: anchorsAfter,
    date: originDateToUse,
  });
  const closestAnchorToUse = getClosestFutureAnchorBetweenTwoAnchors({
    closestAnchorAfterDate,
    closestAnchorBeforeDate,
    date: originDateToUse,
    now,
  });
  const daysBetweenClosestAnchorToUseAndNow = getDaysBetween({
    dateA: closestAnchorToUse,
    dateB: now,
  });
  let nextBillingDate = closestAnchorToUse;

  if (daysBetweenClosestAnchorToUseAndNow < minimumDaysBetweenOrders) {
    const modifiedOriginDate = getDateWithMinimumDaysBetweenOrders({
      closestAnchorToUse,
      daysBetweenClosestAnchorToUseAndNow,
      minimumDaysBetweenOrders,
    });
    const { anchorsAfter } = getAnchorsBeforeAndAfter({
      anchor,
      anchors,
      date: modifiedOriginDate,
    });

    nextBillingDate = getNextAnchorDate({
      anchor,
      anchors: anchorsAfter,
      date: modifiedOriginDate,
    });
  }

  // Check if the next billing date is the same as the original next billing date
  // If it is, get the next anchor date
  if (
    originalNextBillingDate &&
    areTwoDatesSameDay({
      dateA: nextBillingDate,
      dateB: originalNextBillingDate,
    })
  ) {
    const { anchorsAfter } = getAnchorsBeforeAndAfter({
      anchor,
      anchors,
      date: nextBillingDate,
    });

    nextBillingDate = getNextAnchorDate({
      anchor,
      anchors: anchorsAfter,
      date: nextBillingDate,
    });
  }

  const isNextBillingDateToday = areTwoDatesSameDay({
    dateA: nextBillingDate,
    dateB: now,
  });

  const result = nextBillingDate
    .plus({
      hours: isNextBillingDateToday ? 2 : 0,
    })
    .setZone('utc')
    .toISO();

  Settings.defaultZone = 'utc';

  return result;
};
